/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */

import { Page, IUser, UserSearch } from "../../models/Users/UserContracts";
import { showErrorAction } from "../error/errorActionCreator";
import {
  getAllUsersAction,
  setFilteredUserAction,
  setSelectedUserDetailsAction,
  setNoItemAction,
  resetSelectedUserDetailsAction,
  setRolesToAssign,
  setIdps
} from './userActionCreator';
import { generateRandomNumber } from '../../utility/generateRandom';
import {
  getAssignedUsers,
  getUserDetails,
  getEntitlementServiceIdps,
  getIdentityServiceIdps,
  getRealmServiceUsers,
  getDirectoryUserDetails,
  getGraviteeUsers,
} from '../../services/user';
// import { getRealmInfo } from '../../services/setting';
import { getAllRoles } from '../../services/roles';
import { PAGE_NUMBER, PAGE_SIZE, USER_NAME, TITLE, USER_DETAILS_ERROR_MESSAGE } from "../../container/Users/constants";
import { ROLES_FETCH_ERROR } from '../../components/RoleAssignmentDialog/constants';
import interpolateString from '../../utility/stringInterpolation';
import { showHideUserLoaderAction } from "../loader/loaderActionCreator";
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import {
  fetchUsersFailure,
  fetchUsersRequest,
  fetchUsersSuccess
} from "./assignedUsers/assignedUsersActionCreator";
import { MIGRATION_STATUS } from '../../components/AddMachineDialog/contants';

export async function getUsers(dispatch: Function, userSearch: UserSearch, locationName?: string) {
  let filteredUsers: IUser[] = [];
  let selectedItem: any = {};
  const { 'filter-by-value': filterByValue, 'filter-by-key': filterByKey, ...otherParams } = userSearch;
  const searchParams: UserSearch = {
    'page-number': PAGE_NUMBER,
    'page-size': PAGE_SIZE,
    'order-by': USER_NAME,
    ...(filterByValue &&
      filterByValue.length >= 3 && {
      'filter-by-value': filterByValue,
      'filter-by-key': filterByKey
    }),
    ...otherParams
  };
  dispatch(showHideUserLoaderAction(true));
  try {
    const dataList = await getGraviteeUsers(searchParams, locationName);
    dispatch(showHideUserLoaderAction(false));
    if (dataList.usersList.length > 0) {
      filteredUsers = dataList.usersList.map((item: IUser) => {
        item.priority = generateRandomNumber();
        return item;
      });
      filteredUsers = filteredUsers.sort((a, b) => a.userName.localeCompare(b.userName));
      if (searchParams['filter-by-value']) {
        selectedItem = dataList.usersList.filter(f => f.userName === userSearch["filter-by-username"])[0];
        dispatch(getAllUsersAction(dataList.usersList, filteredUsers, selectedItem, dataList.page));
      }
      else {
        selectedItem = filteredUsers[0];
        dispatch(setFilteredUserAction(filteredUsers, selectedItem, dataList.page));
      }
    }
    else {
      dispatch(setNoItemAction(dataList.usersList, filteredUsers, true));
    }
  } catch (_error) {
    const errorMessage = {
      message: interpolateString([TITLE])
    };
    const users: IUser[] = [];
    const page = new Page();
    filteredUsers = [];
    selectedItem = {};
    dispatch(showHideUserLoaderAction(false));
    dispatch(getAllUsersAction(users, filteredUsers, selectedItem, page));
    dispatch(showErrorAction(errorMessage));
  }
}

export async function getUserDetailsById(
  dispatch: Function,
  userId: string,
  _isNativeUser: boolean,
  locationName?: string
) {
  try {
    const userData = await getDirectoryUserDetails(userId, locationName);
    dispatch(setSelectedUserDetailsAction(userData));
    return userData;
  } catch (_error: any) {
    dispatch(resetSelectedUserDetailsAction());
    let message = _error && typeof (_error.message) === "string" ? `${USER_DETAILS_ERROR_MESSAGE}: ${_error.message}` : USER_DETAILS_ERROR_MESSAGE;
    if (
      _error.response &&
      (_error.response.status === 400 || _error.response.status === 500)
    ) {
      if (_error.response.data && _error.response.data.message) {
        message = `${USER_DETAILS_ERROR_MESSAGE} ${_error.response.data.message}`;
      }
    }
    dispatch(
      setSnackBarMessage({
        showSnackbar: true,
        autoHideDuration: 5000,
        message: message,
        severity: 'error'
      })
    );
  }
};

export async function fetchRolesToAssign(dispatch: Function, locationName?: string) {
  try {
    dispatch(showErrorAction({ info: '', message: '' }));
    const roles = await getAllRoles(locationName);
    dispatch(setRolesToAssign(roles));
  } catch (error: any) {
    dispatch(setRolesToAssign([]));
    dispatch(showErrorAction({ message: `${ROLES_FETCH_ERROR}: ${error}` }));
  }
}

export async function fetchAssignedUsers(dispatch: Function, id: number, search: any, locationName?: string) {
  dispatch(fetchUsersRequest());
  try {
    const { entities, meta: { page } } = await getAssignedUsers(id, search, locationName);
    dispatch(fetchUsersSuccess(entities, page));
  } catch (error: any) {
    dispatch(fetchUsersFailure(error));
  }
}

export async function fetchIdps(dispatch: Function) {
  async function retrieveEntitlementServiceIdps() {
    const { idps } = await getEntitlementServiceIdps();
    return idps ?? [];
  }

  async function retrieveIdentityServiceIdps() {
    const { idps } = await getIdentityServiceIdps();
    return idps ?? [];
  }

  // const realmDetails = await getRealmInfo();
  const idps = 'MODERN' === MIGRATION_STATUS.MODERN ? await retrieveIdentityServiceIdps() : await retrieveEntitlementServiceIdps();
  dispatch(setIdps(idps));
  return idps;
}
